import { FC, useEffect, useState, useMemo, useCallback } from 'react';

// API
import apibridge from '../../../apibridge';

// REDUX
import { ToastMessagesSlice } from '../../../store/slice/ToastMessages';
import { useDispatch } from 'react-redux';

// PACKAGES
import { Link, useSearchParams } from 'react-router-dom';
import { Accordion, Fade, Spinner, Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Moment from 'moment';
import moment from 'moment';
import { useImmer } from 'use-immer';

// COMPONENTS
import TitleSection, { TitleSectionType } from '../../../components/TitleSection/TitleSection';
import SvgMask from '../../../components/_Helpers/SvgMask';
import DelayedFadeIn from '../../../components/_Helpers/DelayedFadeIn';
import MarkingButton from '../../../components/Assessment/MarkingButton';
import GraphemeQuestion from '../../../components/Assessment/GraphemeQuestion';
import StudentNavigationReading from './StudentNavigationReading';
import MoveAssessmentReadingModal from './MoveAssessmentReadingModal';

// UTILS
import { guid } from '../../../libs/utils';

// TYPES
import {
	AssessmentAnswerStatus,
	AssessmentEditLlarsAssessmentInfo,
	AssessmentIncorrectNoteType
} from '../../../api/models';

import {
	DynamicFtux,
	FtuxStep,
	getRecordReadingConfig,
	useFtuxHighlight,
	useDynamicFtuxRestart
} from '../../../components/Ftux';

const AssessmentReading: FC = () => {
	const dispatch = useDispatch();

	// Get Details from params
	const [searchParams] = useSearchParams();
	const recordId = searchParams.get('recordId') || '';
	const assessmentName = searchParams.get('assessmentName') || '';
	// Loading State
	const [isLoading, setIsLoading] = useState(false);
	const [isEditing, setIsEditing] = useState(false);

	// State to hide/show control bar
	const [showConfirmationBar, setShowConfirmationBar] = useState(false);

	// Assessment Details
	const [assessmentReadingDetailsData, setAssessmentReadingDetailsData] = useImmer<AssessmentEditLlarsAssessmentInfo>(
		{}
	);

	// Clone state of Assessment Details
	const [assessmentReadingDetailsDataClone, setAssessmentReadingDetailsDataClone] =
		useImmer<AssessmentEditLlarsAssessmentInfo>({});

	const shouldHighlightConductAssessment = useFtuxHighlight('conduct-assessment-button', 'record-reading-ftux');
	const shouldHighlightRecordingDate = useFtuxHighlight('recording-date', 'record-reading-ftux');
	const shouldHighlightStudent = useFtuxHighlight('student-view', 'record-reading-ftux');
	const shouldHighlightGrapheneSpellingLetter = useFtuxHighlight('graphene-spelling-letter', 'record-reading-ftux');
	const shouldHighlightStudentWordMarkingButtons = useFtuxHighlight(
		'student-word-marking-buttons',
		'record-reading-ftux'
	);
	const shouldHighlightNotesView = useFtuxHighlight('notes-view', 'record-reading-ftux');
	const shouldHighlightScore = useFtuxHighlight('score-view', 'record-reading-ftux');
	const { restartTutorial, manualRestartTrigger } = useDynamicFtuxRestart('AssessmentsReading');

	const handleFtuxStepNext = useCallback((step: FtuxStep, stepIndex: number) => {
		// Open Stage 1 accordion when moving to #student-view step
		if (step.target === '#student-view') {
			const stage1Element = document.querySelector('#stage-1');
			const toggleButton = stage1Element?.querySelector<HTMLButtonElement>('.accordion-button');

			if (toggleButton && toggleButton.getAttribute('aria-expanded') !== 'true') {
				toggleButton.click();
			}
		}
	}, []);

	const assessmentReadingConfig = useMemo(() => {
		const config = getRecordReadingConfig(assessmentName === 'GPK' ? 'GPK' : 'non-word');
		return {
			...config,
			onStepNext: handleFtuxStepNext
		};
	}, [assessmentName, handleFtuxStepNext]);

	// Get Details call
	const getAssessmentReadingDetailsData = async () => {
		setIsLoading(true);

		const response = await apibridge.getAssessmentEditLlars(recordId);

		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				setAssessmentReadingDetailsData(response.data.result);

				setAssessmentReadingDetailsDataClone(response.data.result);
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Assessment Details error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}

		setIsLoading(false);
	};

	useEffect(() => {
		if (recordId) {
			getAssessmentReadingDetailsData();
		}
	}, [recordId]); // eslint-disable-line react-hooks/exhaustive-deps

	const {
		recordingDate,
		firstName,
		lastName,
		className,
		stages,
		marked,
		totalQuestions,
		showAttributes,
		incorrectNoteTypes,
		semesterLabel,
		assessmentId,
		assessmentPeriodId,
		classId,
		studentId,
		type
	} = assessmentReadingDetailsData || {};

	// State to record dates
	const [dateSelected, setDateSelected] = useState<string>(recordingDate || new Date().toDateString());

	useEffect(() => {
		if (recordingDate) {
			setDateSelected(recordingDate);
		}
	}, [recordingDate]);

	// Title Props set up
	const titleSectionProps: TitleSectionType = {
		smallTitle: 'LLARS',
		smallSubTitle: assessmentName,
		secondaryTitle: semesterLabel,
		backToText: 'Back',
		backToUrl: `/assessment-management?tabView=Results`
	};

	// EDIT Call
	const confirmEditReadingAssessment = async () => {
		setIsEditing(true);

		const submitData = {
			recordId,
			recordingDate: dateSelected,
			stages: assessmentReadingDetailsData?.stages?.map((stageItem) => ({
				id: stageItem?.id,
				comment: stageItem?.comment,
				answers: stageItem?.questions?.map((questionItem) => ({
					id: questionItem?.id,
					usedDecoding: questionItem?.usedDecoding,
					alternativePronunciation: questionItem?.alternativePronunciation,
					// NOTE: This is to handle the case where the incorrectNoteType is undefined or '-1' or -1
					incorrectNoteType:
						questionItem?.incorrectNoteType === undefined ||
						(questionItem?.incorrectNoteType as string) === '-1' ||
						Number(questionItem?.incorrectNoteType) === -1
							? undefined
							: questionItem?.incorrectNoteType,
					graphemes: questionItem?.graphemes?.map((graphemeItem) => ({
						id: graphemeItem?.id,
						status: graphemeItem?.status
					}))
				}))
			}))
		};

		const response = await apibridge.postAssessmentEditLlars(submitData);

		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				// If Successfully POST, call the GET details endpoint again to update the stage.
				const response = await apibridge.getAssessmentEditLlars(recordId);

				if (response && response.data) {
					if (!response.data.isError && response.data.result) {
						setAssessmentReadingDetailsData(response.data.result);

						setAssessmentReadingDetailsDataClone(response.data.result);
					} else if (response.data.validationErrors) {
						for (const err of response.data.validationErrors) {
							dispatch(
								ToastMessagesSlice.actions.add({
									id: guid(),
									type: 'danger',
									heading: 'Assessment Details error',
									description: err.reason || 'Unknown error'
								})
							);
						}
					}
				}

				setShowConfirmationBar(false);

				dispatch(
					ToastMessagesSlice.actions.add({
						id: guid(),
						type: 'success',
						heading: 'Record saved.',
						description: ''
					})
				);
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Assessment Submission error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}

		setIsEditing(false);
	};

	// STUDENT NAVIGATION STATE
	const [showsStudentNavigationOffCanvas, setShowsStudentNavigationOffCanvas] = useState(false);

	// Function to discard changes (used by student navigation)
	const discardChanges = () => {
		setAssessmentReadingDetailsData(assessmentReadingDetailsDataClone);
		setDateSelected(recordingDate || new Date().toDateString());
		setShowConfirmationBar(false);

		dispatch(
			ToastMessagesSlice.actions.add({
				id: guid(),
				type: 'danger',
				heading: 'Changes to record discarded',
				description: ''
			})
		);
	};

	// MOVE ASSESSMENTS MODAL STATE
	const [showMoveAssessmentsModal, setShowMoveAssessmentsModal] = useState(false);

	return (
		<div className="assessment-page position-relative">
			<TitleSection {...titleSectionProps} isHelpSection={true} onHelpClick={() => restartTutorial()}>
				<div className="d-flex justify-content-between">
					<div className={`d-flex justify-content-start gap-3 ${isLoading ? 'opacity-20 pe-none' : ''}`}>
						<div
							className={`d-flex flex-column justify-content-center ${
								shouldHighlightStudent ? 'highlight-outline rounded-3' : ''
							}`}
							id="student-view"
						>
							<p className="mb-0 body-tiny fw-bold text-shades-500">Student</p>

							<div className="d-flex align-items-center gap-2">
								<p className="mb-0 body-small fw-semibold text-dark-text">
									{firstName} {lastName}
								</p>

								<button className="date-picker-display" onClick={() => setShowsStudentNavigationOffCanvas(true)}>
									<span className="body-small fw-bold text-vivid-blue">Change</span>
								</button>
							</div>
						</div>

						<div className="tab-divider"></div>

						<div className="d-flex flex-column justify-content-center">
							<p className="mb-0 body-tiny fw-bold text-shades-500">Class</p>

							<p className="mb-0 body-small fw-semibold text-dark-text">{className}</p>
						</div>

						<div className="tab-divider"></div>

						<div
							className={`d-flex flex-column justify-content-center ${
								shouldHighlightRecordingDate ? 'highlight-outline rounded-3' : ''
							}`}
							id="recording-date"
						>
							<p className="mb-0 body-tiny fw-bold text-shades-500">Recording date</p>

							<button className="date-picker-display body-small fw-semibold text-dark-text d-flex justify-content-start gap-2">
								<span>{Moment(dateSelected).format('DD/MM/YYYY')}</span>

								<span className="body-small fw-bold text-vivid-blue">Edit</span>

								<input
									className="date-picker"
									type="date"
									max={moment().format('YYYY-MM-DD')}
									value={dateSelected}
									onChange={(e) => {
										setDateSelected(e.target.value as string);

										setShowConfirmationBar(true);
									}}
								/>
							</button>
						</div>
					</div>

					<div className="sort-container d-flex align-items-center">
						<Link
							id="conduct-assessment-button"
							to={`/conduct-assessment?recordId=${recordId}&assessmentName=${assessmentName}`}
							className={`btn text-nowrap ${shouldHighlightConductAssessment ? 'highlight-outline' : ''}`}
						>
							<SvgMask path="/svg/tick-list.svg" width={16} height={16} />

							<span>Conduct assessment</span>
						</Link>

						<div className="assessment-record-counter">
							{marked}/{totalQuestions} Marked
						</div>

						<Dropdown className="assessment-move-record-dropdown" drop="down">
							<Dropdown.Toggle className="btn btn-sm btn-icon btn-dropdown btn-move-record w-auto">
								<SvgMask path="/svg/ellipsis-v.svg" width={24} height={24} />
							</Dropdown.Toggle>

							<Dropdown.Menu align="end">
								<Dropdown.Item
									as="button"
									className="move-record-dropdown-item"
									onClick={() => setShowMoveAssessmentsModal(true)}
								>
									Move record
								</Dropdown.Item>
							</Dropdown.Menu>
						</Dropdown>
					</div>
				</div>
			</TitleSection>

			<div className="container">
				{isLoading ? (
					<div className="d-flex align-items-center justify-content-center position-relative w-100">
						<DelayedFadeIn>
							<Spinner animation="border" role="status">
								<span className="visually-hidden">Loading...</span>
							</Spinner>
						</DelayedFadeIn>
					</div>
				) : (
					<Accordion className="book-stage-accordion assessment-accordion gap-3-5 d-flex flex-column" alwaysOpen>
						{stages?.length &&
							stages.map((stageItem, stageIndex) => {
								return (
									<Accordion.Item
										key={stageItem.id}
										id={`stage-${stageItem.stageName}`}
										eventKey={`${stageItem.stageName}-${stageItem.id}`}
										className={`stage-${stageItem.stageName}`}
									>
										<Accordion.Header>
											<div
												className={`stage-label btn-reset d-flex align-items-center justify-content-between w-100 gap-2-5 stage-${stageItem.stageName}`}
											>
												<h4 className="m-0">Stage {stageItem.stageName}</h4>
												<div
													id={`score-view`}
													className={`p-2 mx-2 ${
														stageItem.stageName === '1' && shouldHighlightScore ? 'highlight-box rounded-3' : ''
													}`}
												>
													<h4 className={`m-0 mx-3 score-text `}>
														Score {stageItem.score !== undefined ? stageItem.score : '-'}/{stageItem.totalScore}{' '}
													</h4>
												</div>
											</div>
										</Accordion.Header>

										<Accordion.Body className="d-flex flex-column">
											{/* STAGE CONTENT */}
											{stageItem.questions?.length &&
												stageItem.questions.map((questionItem, questionIndex) => {
													const usedDecoding = questionItem.usedDecoding;
													const alternativePronunciation = questionItem.alternativePronunciation;

													// Check to see if there is at least One Unsuccessful Grapheme
													const isThereIncorrectGrapheme = questionItem?.graphemes?.some(
														(graphemeItem) => graphemeItem.status === AssessmentAnswerStatus.Unsuccessful
													);

													// Check to see if there is no Marked Grapheme
													const isThereMarkedGrapheme = questionItem?.graphemes?.some(
														(graphemeItem) =>
															graphemeItem.status === AssessmentAnswerStatus.Successful ||
															graphemeItem.status === AssessmentAnswerStatus.Unsuccessful
													);

													return (
														<div className="row assessment-accordion-row" key={questionItem.id || questionIndex}>
															<div
																className={`${
																	showAttributes ? 'col-12 col-lg-7' : 'col-12 col-lg-8'
																} d-flex flex-wrap gap-2 justify-content-between align-items-center border-right border-bottom-lg py-4 px-4`}
															>
																<div className="d-flex align-items-center flex-wrap gap-2">
																	{questionItem?.graphemes?.length &&
																		questionItem.graphemes.map((graphemeItem, graphemeIndex) => {
																			// Only Stage 1, first question, first grapheme gets the FTUX target ID and highlight
																			const isStage1FirstQuestionFirstGrapheme =
																				stageItem.stageName === '1' && questionIndex === 0 && graphemeIndex === 0;
																			const firstGraphneToHighlight =
																				isStage1FirstQuestionFirstGrapheme && shouldHighlightGrapheneSpellingLetter;
																			return (
																				<GraphemeQuestion
																					id={
																						isStage1FirstQuestionFirstGrapheme ? 'graphene-spelling-letter' : undefined
																					}
																					shouldHighlight={firstGraphneToHighlight}
																					key={graphemeItem.id || graphemeIndex}
																					label={graphemeItem.text || ''}
																					state={
																						graphemeItem.status === AssessmentAnswerStatus.Successful
																							? 'success'
																							: graphemeItem.status === AssessmentAnswerStatus.Unsuccessful
																							? 'danger'
																							: 'default'
																					}
																					isSmallSize
																					onClick={() => {
																						setAssessmentReadingDetailsData((draft) => {
																							const draftStage = draft?.stages?.find(
																								(draftStageItem) => draftStageItem.id === stageItem.id
																							);

																							const draftQuestion = draftStage?.questions?.find(
																								(draftQuestionItem) => draftQuestionItem.id === questionItem.id
																							);

																							const draftGrapheme = draftQuestion?.graphemes?.find(
																								(draftGraphemeItem) => draftGraphemeItem.id === graphemeItem.id
																							);

																							if (draftGrapheme && draftGrapheme.status !== undefined) {
																								if (
																									draftGrapheme.status === AssessmentAnswerStatus.Unsuccessful ||
																									draftGrapheme.status === AssessmentAnswerStatus.Pending
																								) {
																									draftGrapheme.status = AssessmentAnswerStatus.Successful;
																								} else {
																									draftGrapheme.status = AssessmentAnswerStatus.Unsuccessful;
																								}
																							}

																							// This is to reset the Incorrect Note dropdown to undefined value
																							if (
																								draftQuestion?.graphemes?.every(
																									(graphemeItem) =>
																										graphemeItem.status === AssessmentAnswerStatus.Successful
																								)
																							) {
																								draftQuestion.incorrectNoteType = '-1' as AssessmentIncorrectNoteType;
																							}
																						});

																						setShowConfirmationBar(true);
																					}}
																				/>
																			);
																		})}
																</div>

																<div
																	id={
																		stageItem.stageName === '1' && questionIndex === 0
																			? 'student-word-marking-buttons'
																			: undefined
																	}
																	className={`d-flex align-items-center gap-3 ms-auto ${
																		stageItem.stageName === '1' &&
																		questionIndex === 0 &&
																		shouldHighlightStudentWordMarkingButtons
																			? 'highlight-outline rounded-3'
																			: ''
																	}`}
																>
																	<MarkingButton
																		state="incorrect"
																		onClick={() => {
																			setAssessmentReadingDetailsData((draft) => {
																				const draftStage = draft?.stages?.find(
																					(draftStageItem) => draftStageItem.id === stageItem.id
																				);

																				const draftQuestion = draftStage?.questions?.find(
																					(draftQuestionItem) => draftQuestionItem.id === questionItem.id
																				);

																				draftQuestion?.graphemes?.forEach((graphemeItem) => {
																					graphemeItem.status = AssessmentAnswerStatus.Unsuccessful;
																				});
																			});

																			setShowConfirmationBar(true);
																		}}
																	/>

																	<MarkingButton
																		state="correct"
																		onClick={() => {
																			setAssessmentReadingDetailsData((draft) => {
																				const draftStage = draft?.stages?.find(
																					(draftStageItem) => draftStageItem.id === stageItem.id
																				);

																				const draftQuestion = draftStage?.questions?.find(
																					(draftQuestionItem) => draftQuestionItem.id === questionItem.id
																				);

																				draftQuestion?.graphemes?.forEach((graphemeItem) => {
																					graphemeItem.status = AssessmentAnswerStatus.Successful;
																				});

																				// This is to reset the Incorrect Note dropdown to undefined value
																				if (draftQuestion) {
																					draftQuestion.incorrectNoteType = '-1' as AssessmentIncorrectNoteType;
																				}
																			});

																			setShowConfirmationBar(true);
																		}}
																	/>

																	{/* Reset current questions Graphemes */}
																	<OverlayTrigger
																		placement="top"
																		overlay={
																			<Tooltip
																				id={`assessment-reset-btn-${questionItem.id || questionIndex}`}
																				style={{ marginBottom: '1px' }}
																			>
																				Reset
																			</Tooltip>
																		}
																	>
																		<button
																			className="assessment-marking-reset-button"
																			disabled={!isThereMarkedGrapheme}
																			onClick={() => {
																				setAssessmentReadingDetailsData((draft) => {
																					const draftStage = draft?.stages?.find(
																						(draftStageItem) => draftStageItem.id === stageItem.id
																					);

																					const draftQuestion = draftStage?.questions?.find(
																						(draftQuestionItem) => draftQuestionItem.id === questionItem.id
																					);

																					// Reset Graphemes
																					draftQuestion?.graphemes?.forEach((graphemeItem) => {
																						graphemeItem.status = AssessmentAnswerStatus.Pending;
																					});

																					if (draftQuestion) {
																						// Reset Attributes
																						draftQuestion.usedDecoding = undefined;
																						draftQuestion.alternativePronunciation = undefined;

																						// Reset Incorrect Note
																						draftQuestion.incorrectNoteType = '-1' as AssessmentIncorrectNoteType;
																					}
																				});

																				setShowConfirmationBar(true);
																			}}
																		>
																			<SvgMask path={'/svg/redo.svg'} className="icon" width={32} height={32} />
																		</button>
																	</OverlayTrigger>
																</div>
															</div>

															{showAttributes && (
																<div className="col-3 col-lg-2 d-flex flex-column align-align-items-start gap-1 border-right py-4 px-4">
																	<span className="body-small fw-bold text-dark-text">Attributes</span>

																	<div className="d-flex align-items-center gap-2">
																		<input
																			className="form-check"
																			type="checkbox"
																			name={`stage-${stageItem.id}-question-${questionItem.id}-attribute-decoded`}
																			id={`stage-${stageItem.id}-question-${questionItem.id}-attribute-decoded`}
																			checked={questionItem.usedDecoding}
																			disabled={!isThereMarkedGrapheme}
																			onChange={() => {
																				setAssessmentReadingDetailsData((draft) => {
																					const draftStage = draft?.stages?.find(
																						(draftStageItem) => draftStageItem.id === stageItem.id
																					);

																					const draftQuestion = draftStage?.questions?.find(
																						(draftQuestionItem) => draftQuestionItem.id === questionItem.id
																					);

																					if (draftQuestion) {
																						if (usedDecoding === undefined) {
																							draftQuestion.usedDecoding = true;
																						} else {
																							draftQuestion.usedDecoding = !usedDecoding;
																						}
																					}
																				});

																				setShowConfirmationBar(true);
																			}}
																		/>
																		<label
																			htmlFor={`stage-${stageItem.id}-question-${questionItem.id}-attribute-decoded`}
																			className={`fw-medium ${isThereMarkedGrapheme ? '' : 'opacity-20 pe-none'}`}
																		>
																			Decoded
																		</label>
																	</div>

																	<div className="d-flex align-items-center gap-2">
																		<input
																			className="form-check"
																			type="checkbox"
																			name={`stage-${stageItem.id}-question-${questionItem.id}-attribute-read-auto`}
																			id={`stage-${stageItem.id}-question-${questionItem.id}-attribute-read-auto`}
																			checked={questionItem.alternativePronunciation}
																			disabled={!isThereMarkedGrapheme}
																			onChange={() => {
																				setAssessmentReadingDetailsData((draft) => {
																					const draftStage = draft?.stages?.find(
																						(draftStageItem) => draftStageItem.id === stageItem.id
																					);

																					const draftQuestion = draftStage?.questions?.find(
																						(draftQuestionItem) => draftQuestionItem.id === questionItem.id
																					);

																					if (draftQuestion) {
																						if (alternativePronunciation === undefined) {
																							draftQuestion.alternativePronunciation = true;
																						} else {
																							draftQuestion.alternativePronunciation = !alternativePronunciation;
																						}
																					}
																				});

																				setShowConfirmationBar(true);
																			}}
																		/>
																		<label
																			htmlFor={`stage-${stageItem.id}-question-${questionItem.id}-attribute-read-auto`}
																			className={`fw-medium ${isThereMarkedGrapheme ? '' : 'opacity-20 pe-none'}`}
																		>
																			Read auto
																		</label>
																	</div>
																</div>
															)}

															<div
																className={`${
																	showAttributes ? 'col-9 col-lg-3' : 'col-12 col-lg-4'
																} d-flex flex-column align-align-items-start py-4 px-4`}
															>
																<div
																	id={stageItem.stageName === '1' && questionIndex === 0 ? 'notes-view' : undefined}
																	className={`form-group w-100  ${isThereIncorrectGrapheme ? '' : 'disable'} ${
																		stageItem.stageName === '1' && questionIndex === 0 && shouldHighlightNotesView
																			? 'highlight-outline rounded-3'
																			: ''
																	}`}
																>
																	<label htmlFor="incorrect-note" className="body-small fw-bold text-dark-text mb-1">
																		<strong>Incorrect note</strong>
																	</label>

																	<select
																		value={questionItem.incorrectNoteType || '-1'}
																		className="form-select"
																		name={`stage-${stageItem.id}-question-${questionItem.id}-incorrect-note`}
																		id={`stage-${stageItem.id}-question-${questionItem.id}-incorrect-note`}
																		onChange={(e) => {
																			setAssessmentReadingDetailsData((draft) => {
																				const draftStage = draft?.stages?.find(
																					(draftStageItem) => draftStageItem.id === stageItem.id
																				);

																				const draftQuestion = draftStage?.questions?.find(
																					(draftQuestionItem) => draftQuestionItem.id === questionItem.id
																				);

																				if (draftQuestion) {
																					draftQuestion.incorrectNoteType = e.target
																						.value as AssessmentIncorrectNoteType;
																				}
																			});

																			setShowConfirmationBar(true);
																		}}
																	>
																		<option value="-1" disabled>
																			Select from list
																		</option>

																		{incorrectNoteTypes?.map((noteTypeItem, noteTypeIndex) => {
																			return (
																				<option value={noteTypeItem.value} key={noteTypeItem.value || noteTypeIndex}>
																					{noteTypeItem.label}
																				</option>
																			);
																		})}
																	</select>
																</div>
															</div>
														</div>
													);
												})}

											{/* COMMENT */}
											<div className="row assessment-accordion-row">
												<div className="col-12">
													<div className="d-flex flex-column align-align-items-start py-3 px-3">
														<label
															htmlFor={`stage-${stageItem.id}-comment`}
															className="body-small fw-bold text-dark-text mb-1"
														>
															<strong>Comments</strong>
														</label>

														<textarea
															id={`stage-${stageItem.id}-comment`}
															value={stageItem.comment || ''}
															placeholder="Add a comment or note about the assessment of this stage"
															onChange={(e) => {
																setAssessmentReadingDetailsData((draft) => {
																	const draftStage = draft?.stages?.find(
																		(draftStageItem) => draftStageItem.id === stageItem.id
																	);

																	if (draftStage) {
																		draftStage.comment = e.target.value;
																	}
																});

																setShowConfirmationBar(true);
															}}
														></textarea>
													</div>
												</div>
											</div>
										</Accordion.Body>
									</Accordion.Item>
								);
							})}
					</Accordion>
				)}
			</div>

			{/* CONFIRMATION BAR */}
			<Fade in={showConfirmationBar} mountOnEnter unmountOnExit>
				<div className="assessment-confirmation-bar">
					<div className="container">
						<div className="assessment-confirmation-content">
							<p className="mb-0 fw-semibold">
								Unsaved changes have been made to this student record. Do you wish to save?
							</p>

							<div className="d-flex gap-2">
								<button onClick={() => confirmEditReadingAssessment()} className="btn text-nowrap">
									{isEditing ? (
										<Spinner animation="border" size="sm" />
									) : (
										<>
											<SvgMask path={'/svg/save.svg'} width={16} height={16} />
											Save changes
										</>
									)}
								</button>

								<button
									className="btn btn-white text-nowrap"
									onClick={() => {
										setAssessmentReadingDetailsData(assessmentReadingDetailsDataClone);

										setDateSelected(recordingDate || new Date().toDateString());

										setShowConfirmationBar(false);

										dispatch(
											ToastMessagesSlice.actions.add({
												id: guid(),
												type: 'danger',
												heading: 'Changes to record discarded',
												description: ''
											})
										);
									}}
								>
									Discard changes
								</button>
							</div>
						</div>
					</div>
				</div>
			</Fade>

			{/* STUDENT NAVIGATION COMPONENT */}
			<StudentNavigationReading
				showOffCanvas={showsStudentNavigationOffCanvas}
				onHideOffCanvas={() => setShowsStudentNavigationOffCanvas(false)}
				showConfirmationBar={showConfirmationBar}
				onConfirmEditAssessment={confirmEditReadingAssessment}
				onDiscardChanges={discardChanges}
				assessmentName={assessmentName}
				semesterLabel={semesterLabel}
				assessmentId={assessmentId}
				assessmentPeriodId={assessmentPeriodId}
				classId={classId}
				studentId={studentId}
				type={type}
			/>

			{/* MOVE ASSESSMENTS MODAL */}
			<MoveAssessmentReadingModal
				show={showMoveAssessmentsModal}
				onHide={() => setShowMoveAssessmentsModal(false)}
				firstName={firstName}
				lastName={lastName}
				assessmentName={assessmentName}
				assessmentPeriodId={assessmentPeriodId}
				studentId={studentId}
				assessmentId={assessmentId}
				type={type}
				onMoveSuccess={getAssessmentReadingDetailsData}
			/>

			<DynamicFtux
				config={assessmentReadingConfig}
				tutorialType="AssessmentsReading"
				manualRestartTrigger={manualRestartTrigger}
			/>
		</div>
	);
};

export default AssessmentReading;
